import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast.tsx';
import { Modal } from '../components/Modal';
import type { Cliente, CreateClienteRequest, UpdateClienteRequest } from '../types';
import { clientesApi } from '../services/api';
import { Plus, Search, UserX, UserCheck, Edit, Trash2 } from 'lucide-react';

interface ClienteForm {
  nome: string;
  telefone: string;
  email: string;
  nif: string;
  morada: string;
  notas: string;
  clienteEventual: boolean;
  motivoBloqueio: string;
}

const emptyForm: ClienteForm = { nome: '', telefone: '', email: '', nif: '', morada: '', notas: '', clienteEventual: false, motivoBloqueio: '' };

export function ClientesPage() {
  const { isAdmin } = useAuth();
  const { success, error } = useToast();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<Cliente | null>(null);
  const [form, setForm] = useState<ClienteForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const loadClientes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientesApi.getAll(search, 1, 100);
      if (response.success && response.data) {
        setClientes(response.data.items);
      }
    } catch {
      error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  }, [search, error]);

  useEffect(() => {
    const timeout = setTimeout(loadClientes, 300);
    return () => clearTimeout(timeout);
  }, [loadClientes]);

  const openNovo = () => {
    setForm(emptyForm);
    setEditando(null);
    setFormError('');
    setModalOpen(true);
  };

  const openEditar = (cliente: Cliente) => {
    setForm({
      nome: cliente.nome,
      telefone: cliente.telefone || '',
      email: cliente.email || '',
      nif: cliente.nif || '',
      morada: cliente.morada || '',
      notas: cliente.notas || '',
      clienteEventual: cliente.clienteEventual,
      motivoBloqueio: cliente.motivoBloqueio || '',
    });
    setEditando(cliente);
    setFormError('');
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim()) {
      setFormError('O nome é obrigatório');
      return;
    }

    setSaving(true);
    try {
      if (editando) {
        const request: UpdateClienteRequest = {
          nome: form.nome.trim(),
          telefone: form.telefone.trim() || undefined,
          email: form.email.trim() || undefined,
          nif: form.nif.trim() || undefined,
          morada: form.morada.trim() || undefined,
          notas: form.notas.trim() || undefined,
          clienteEventual: form.clienteEventual,
          motivoBloqueio: editando.bloqueado ? (form.motivoBloqueio.trim() || null) : null,
          versao: editando.versao || 1,
        };
        const response = await clientesApi.update(editando.id, request);
        if (response.success) {
          success('Cliente atualizado com sucesso');
          setModalOpen(false);
          loadClientes();
        } else {
          setFormError(response.message || 'Erro ao atualizar cliente');
        }
      } else {
        const request: CreateClienteRequest = {
          nome: form.nome.trim(),
          telefone: form.telefone.trim() || undefined,
          email: form.email.trim() || undefined,
          nif: form.nif.trim() || undefined,
          morada: form.morada.trim() || undefined,
          notas: form.notas.trim() || undefined,
          clienteEventual: form.clienteEventual,
        };
        const response = await clientesApi.create(request);
        if (response.success) {
          success('Cliente criado com sucesso');
          setModalOpen(false);
          loadClientes();
        } else {
          setFormError(response.message || 'Erro ao criar cliente');
        }
      }
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Erro ao guardar cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleBloquear = async (cliente: Cliente) => {
    try {
      const vaiBloquear = !cliente.bloqueado;
      const motivo = vaiBloquear
        ? (window.prompt('Motivo de bloqueio (opcional)', cliente.motivoBloqueio || '') ?? '')
        : '';
      const response = await clientesApi.bloquear(cliente.id, { bloqueado: vaiBloquear, motivo, versao: cliente.versao || 1 });
      if (response.success) {
        success(cliente.bloqueado ? 'Cliente desbloqueado' : 'Cliente bloqueado');
        loadClientes();
      }
    } catch {
      error('Erro ao atualizar cliente');
    }
  };

  const handleEliminar = async (cliente: Cliente) => {
    if (!confirm(`Eliminar o cliente "${cliente.nome}"? Esta ação não pode ser revertida.`)) return;
    try {
      const response = await clientesApi.delete(cliente.id);
      if (response.success) {
        success('Cliente eliminado');
        loadClientes();
      } else {
        error(response.message || 'Erro ao eliminar cliente');
      }
    } catch {
      error('Erro ao eliminar cliente');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Clientes</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={openNovo}>
            <Plus className="w-4 h-4" />
            Novo Cliente
          </button>
        </div>
      </div>

      <div className="card">
        <div className="search-bar">
          <Search className="w-5 h-5" />
          <input
            type="text"
            placeholder="Pesquisar por nome, telefone ou email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input"
          />
        </div>

        {loading ? (
          <div className="page-loading">
            <div className="spinner"></div>
            <p>A carregar clientes...</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Telefone</th>
                  <th>Email</th>
                  <th>Estado</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map(cliente => (
                  <tr key={cliente.id}>
                    <td>{cliente.nome}</td>
                    <td>{cliente.telefone || '—'}</td>
                    <td>{cliente.email || '—'}</td>
                    <td>
                      <span className={`badge ${cliente.bloqueado ? 'badge-danger' : 'badge-success'}`}>
                        {cliente.bloqueado ? 'Bloqueado' : 'Ativo'}
                      </span>
                      {cliente.clienteEventual && (
                        <span className="badge badge-neutral" style={{ marginLeft: 6 }}>Eventual</span>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        {!cliente.clienteEventual && (
                          <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEditar(cliente)} aria-label="Editar">
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          className={`btn btn-ghost btn-sm btn-icon ${cliente.bloqueado ? 'text-green-600' : 'text-red-600'}`}
                          onClick={() => handleBloquear(cliente)}
                          aria-label={cliente.bloqueado ? 'Desbloquear' : 'Bloquear'}
                        >
                          {cliente.bloqueado ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                        </button>
                        {isAdmin && !cliente.clienteEventual && (
                          <button className="btn btn-ghost btn-sm btn-icon text-red-600" onClick={() => handleEliminar(cliente)} aria-label="Eliminar">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && clientes.length === 0 && (
          <div className="empty-state">
            <Search className="w-12 h-12 text-gray-300" />
            <h3>Nenhum cliente encontrado</h3>
            <p>Adicione o primeiro cliente</p>
            <button className="btn btn-primary mt-4" onClick={openNovo}>
              <Plus className="w-4 h-4" />
              Novo Cliente
            </button>
          </div>
        )}
      </div>

      <Modal
        title={editando ? 'Editar Cliente' : 'Novo Cliente'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button type="submit" form="cliente-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'A guardar...' : editando ? 'Atualizar' : 'Criar'}
            </button>
          </>
        }
      >
        <form id="cliente-form" onSubmit={handleSubmit} noValidate>
          {formError && (
            <div className="form-error" role="alert">
              {formError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="nome" className="label">Nome <span className="required">*</span></label>
            <input
              type="text"
              id="nome"
              className="input"
              value={form.nome}
              onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
              placeholder="Nome do cliente"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="telefone" className="label">Telefone</label>
              <input
                type="tel"
                id="telefone"
                className="input"
                value={form.telefone}
                onChange={e => setForm(p => ({ ...p, telefone: e.target.value }))}
                placeholder="9xxxxxxxx"
              />
            </div>
            <div className="form-group">
              <label htmlFor="email" className="label">Email</label>
              <input
                type="email"
                id="email"
                className="input"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="email@exemplo.pt"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nif" className="label">NIF</label>
              <input
                type="text"
                id="nif"
                className="input"
                value={form.nif}
                onChange={e => setForm(p => ({ ...p, nif: e.target.value }))}
                placeholder="123456789"
              />
            </div>
            <div className="form-group">
              <label htmlFor="morada" className="label">Morada</label>
              <input
                type="text"
                id="morada"
                className="input"
                value={form.morada}
                onChange={e => setForm(p => ({ ...p, morada: e.target.value }))}
                placeholder="Morada"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notas" className="label">Notas</label>
            <textarea
              id="notas"
              className="input"
              value={form.notas}
              onChange={e => setForm(p => ({ ...p, notas: e.target.value }))}
              rows={3}
              placeholder="Observações sobre o cliente..."
            />
          </div>

          <div className="form-group form-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.clienteEventual}
                onChange={e => setForm(p => ({ ...p, clienteEventual: e.target.checked }))}
              />
              <span>Cliente eventual</span>
            </label>
          </div>

          {(editando?.bloqueado || form.motivoBloqueio) && (
            <div className="form-group">
              <label htmlFor="motivoBloqueio" className="label">Motivo de bloqueio</label>
              <input
                type="text"
                id="motivoBloqueio"
                className="input"
                value={form.motivoBloqueio}
                onChange={e => setForm(p => ({ ...p, motivoBloqueio: e.target.value }))}
                disabled={!editando?.bloqueado}
                placeholder="Motivo do bloqueio..."
              />
            </div>
          )}
        </form>
      </Modal>
    </div>
  );
}